import { parseBlocks, type ContentBlock } from '@sazerac/design-tokens';
import { usePage, useSite } from '../api/queries';
import { usePageMeta } from '../hooks/usePageMeta';
import { ErrorState } from '../components/ui';
import { renderBlock } from '../blocks/registry';
import { BlockMediaContext } from '../blocks/useBlockMedia';

/**
 * The home page is assembled from the CMS block document for the `home` page, so every
 * section, heading and image is editable in the dashboard while the layout and order
 * stay exactly as approved.
 */
export function HomePage() {
  const { data: page, isLoading, isError, error, refetch } = usePage('home');
  const { data: site } = useSite();

  usePageMeta({
    title: page?.seoTitle ?? 'SAZERAC',
    description:
      page?.seoDescription ??
      'A refined café and bar experience shaped by exceptional drinks, warm hospitality and unforgettable moments.',
    image: page?.ogImage?.url,
    canonicalPath: '/',
  });

  if (isLoading) return <HomeSkeleton />;

  if (isError) {
    return (
      <div className="sz-page">
        <ErrorState error={error} onRetry={() => void refetch()} title="We could not load the homepage" />
      </div>
    );
  }

  const blocks: ContentBlock[] = parseBlocks(page?.bodyJson);

  if (blocks.length === 0) {
    return (
      <div className="sz-page">
        <div className="sz-notice">
          This page has no published content yet. Publish the homepage from the dashboard to see it here.
        </div>
      </div>
    );
  }

  return (
    <BlockMediaContext.Provider value={page?.media ?? {}}>
      {blocks.map((block, index) => renderBlock(block, { index, site }))}
    </BlockMediaContext.Provider>
  );
}

function HomeSkeleton() {
  return (
    <div>
      <div className="sz-skeleton" style={{ height: '100vh' }} aria-hidden="true" />
      <div className="sz-section">
        <div className="sz-section__inner" style={{ display: 'grid', gap: 24 }}>
          <div className="sz-skeleton" style={{ height: 40, width: '40%' }} />
          <div className="sz-skeleton" style={{ height: 280 }} />
        </div>
      </div>
      <span className="sz-visually-hidden" role="status">
        Loading the homepage…
      </span>
    </div>
  );
}
